import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { handleNativeAuthIdentityChange } from "./nativeAuthIdentityChange";
import { isRealNativePlatform } from "./platform";

/**
 * Completes the native Auth0 round trip.
 *
 * After the user authenticates in the system browser, Auth0 deep-links back into
 * the app with the custom scheme:
 *   ai.cohesive.mobile://{auth0-domain}/capacitor/ai.cohesive.mobile/callback?code=…&state=…
 *
 * This listener catches that URL — both on cold start (`App.getLaunchUrl()`) and
 * while running (`appUrlOpen`) — finishes the PKCE exchange with
 * `handleRedirectCallback`, navigates to the intended `returnTo`, and closes the
 * browser sheet. Logout return URLs (no `code`) just close the sheet.
 *
 * This is also the only moment the app learns that the signed-in identity
 * changed — an org or account switch is this same round trip, finished in
 * place with no page reload — so it runs
 * {@link handleNativeAuthIdentityChange}.
 *
 * Renders nothing and no-ops on web / VITE_FORCE_NATIVE.
 */
export function NativeAppUrlListener() {
  const { handleRedirectCallback } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isRealNativePlatform()) return;

    let cancelled = false;
    let removeListener: (() => void) | undefined;

    const closeBrowser = async (): Promise<void> => {
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.close();
      } catch {
        /* browser sheet may already be closed — ignore */
      }
    };

    const handleUrl = async (url: string | undefined): Promise<void> => {
      if (!url || !url.includes("/capacitor/")) return;

      if (url.includes("code=") && url.includes("state=")) {
        try {
          const result = await handleRedirectCallback(url);
          // The token cache now holds the org/account the user just chose.
          // Nothing else on this path notices, so fan the change out before
          // navigating.
          handleNativeAuthIdentityChange();
          const returnTo =
            (result?.appState as { returnTo?: string } | undefined)?.returnTo ??
            "/";
          if (!cancelled) navigate(returnTo);
        } catch {
          /* exchange failed (e.g. reused code) — user can retry login */
        } finally {
          await closeBrowser();
        }
      } else {
        // Logout or error return — just dismiss the browser sheet.
        await closeBrowser();
      }
    };

    const setup = async (): Promise<void> => {
      const { App } = await import("@capacitor/app");
      // The app may have been cold-launched by the callback deep link.
      const launch = await App.getLaunchUrl();
      await handleUrl(launch?.url);

      const sub = await App.addListener("appUrlOpen", (event) => {
        void handleUrl(event.url);
      });
      removeListener = () => void sub.remove();
    };

    void setup();

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [handleRedirectCallback, navigate]);

  return null;
}
